<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">
        Competitor Analysis Results
      </h3>
      <button
        @click="$emit('proceed-to-quality')"
        class="inline-flex items-center px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium">
        Review Quality Profile
      </button>
    </div>

    <div
      v-if="benchmarkConfidence?.warnings?.length"
      class="mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
      <div class="flex flex-wrap items-center gap-2 mb-2">
        <span class="text-xs font-medium text-amber-800 dark:text-amber-300">
          Benchmark confidence: {{ benchmarkConfidence.confidence_label }} ({{ benchmarkConfidence.confidence_score }}%)
        </span>
        <span class="text-xs text-amber-700 dark:text-amber-400">
          {{ benchmarkConfidence.analyzed_count }}/{{ benchmarkConfidence.selected_count }} analyzed
        </span>
      </div>
      <p v-for="warning in benchmarkConfidence.warnings" :key="warning" class="text-xs text-amber-800 dark:text-amber-300">
        {{ warning }}
      </p>
    </div>

    <div v-if="failedCompetitors.length" class="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
      <h4 class="text-xs font-medium text-red-800 dark:text-red-300 mb-2">
        Failed Competitor Extractions
      </h4>
      <div v-for="comp in failedCompetitors" :key="comp.id" class="text-xs text-red-700 dark:text-red-300">
        {{ comp.domain || comp.url }}: {{ comp.error_message || 'Extraction failed' }}
      </div>
    </div>

    <!-- Dimension tabs -->
    <div class="border-b border-gray-200 dark:border-gray-700 mb-4">
      <nav class="flex gap-4 -mb-px overflow-x-auto">
        <button
          v-for="dim in dimensions"
          :key="dim.key"
          @click="activeDimension = dim.key"
          :class="[
            'pb-2 px-1 text-xs font-medium border-b-2 whitespace-nowrap',
            activeDimension === dim.key
              ? 'border-primary-500 text-primary-600 dark:text-primary-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
          ]">
          {{ dim.label }}
        </button>
      </nav>
    </div>

    <!-- Headings -->
    <div v-if="activeDimension === 'headings'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-3">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
          <div class="flex items-center gap-2">
            <span class="text-xs text-gray-500">SEO: {{ comp.seo_heading_percentage }}%</span>
            <span :class="gradeBadge(comp.heading_grade)" class="px-2 py-0.5 rounded text-xs font-bold">{{ comp.heading_grade }}</span>
          </div>
        </div>
        <!-- Anti-patterns -->
        <div v-if="comp.anti_patterns && comp.anti_patterns.length" class="mb-2">
          <span v-for="(ap, i) in comp.anti_patterns" :key="i" class="inline-block mr-2 mb-1 px-2 py-0.5 rounded text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
            {{ ap.message }}
          </span>
        </div>
        <!-- Heading tree -->
        <div v-if="comp.heading_analysis && comp.heading_analysis.classified_headings" class="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 space-y-1">
          <div
            v-for="(h, i) in comp.heading_analysis.classified_headings"
            :key="i"
            :style="{ paddingLeft: `${(h.level - 1) * 16}px` }"
            class="text-sm flex items-center gap-2">
            <span class="text-xs text-gray-400 w-6">H{{ h.level }}</span>
            <span :class="classificationColor(h.classification)">
              {{ classificationIcon(h.classification) }}
            </span>
            <span class="text-gray-700 dark:text-gray-300" :class="{ 'font-medium': h.has_keyword }">
              {{ h.text }}
            </span>
            <span v-if="h.is_question" class="text-xs text-blue-500">Q</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Titles & Meta -->
    <div v-else-if="activeDimension === 'titles'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-3">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
        </div>
        <div v-if="comp.title_meta_analysis" class="space-y-3">
          <div>
            <dt class="text-xs text-gray-500 uppercase">
              Title Tag
            </dt>
            <dd class="text-sm mt-1">
              {{ comp.title_meta_analysis.title?.meta_title || 'N/A' }}
            </dd>
            <div class="flex gap-2 mt-1">
              <span class="text-xs text-gray-400">{{ comp.title_meta_analysis.title?.length || 0 }} chars</span>
              <span v-if="comp.title_meta_analysis.title?.rewritten_by_google" class="text-xs text-amber-600">Google rewrote</span>
              <span :class="gradeBadge(comp.title_meta_analysis.title?.grade)" class="px-1.5 py-0.5 rounded text-xs font-bold">{{ comp.title_meta_analysis.title?.grade }}</span>
            </div>
          </div>
          <div>
            <dt class="text-xs text-gray-500 uppercase">
              Meta Description
            </dt>
            <dd class="text-sm mt-1">
              {{ comp.title_meta_analysis.meta?.text || 'N/A' }}
            </dd>
            <div class="flex gap-2 mt-1">
              <span class="text-xs text-gray-400">{{ comp.title_meta_analysis.meta?.length || 0 }} chars</span>
              <span :class="gradeBadge(comp.title_meta_analysis.meta?.grade)" class="px-1.5 py-0.5 rounded text-xs font-bold">{{ comp.title_meta_analysis.meta?.grade }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Images -->
    <div v-else-if="activeDimension === 'images'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
          <span class="text-xs text-gray-500">{{ comp.image_analysis?.summary?.total || 0 }} images, {{ comp.image_analysis?.summary?.alt_percentage || 0 }}% with alt</span>
        </div>
      </div>
    </div>

    <!-- Links -->
    <div v-else-if="activeDimension === 'links'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
        </div>
        <div v-if="comp.link_analysis?.summary" class="flex gap-3 text-xs">
          <span class="text-gray-500">{{ comp.link_analysis.internal_link_count || 0 }} internal</span>
          <span class="text-gray-500">{{ comp.link_analysis.summary.total_external || 0 }} external</span>
          <span class="text-green-600">{{ comp.link_analysis.summary.authoritative || 0 }} authoritative</span>
          <span v-if="comp.link_analysis.summary.spam > 0" class="text-red-500">{{ comp.link_analysis.summary.spam }} spam</span>
        </div>
      </div>
    </div>

    <!-- Readability -->
    <div v-else-if="activeDimension === 'readability'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
        </div>
        <div v-if="comp.readability_metrics && !comp.readability_metrics.error" class="grid grid-cols-3 gap-3">
          <div v-for="(val, key) in comp.readability_metrics" :key="key" class="text-center">
            <div class="text-lg font-bold text-gray-900 dark:text-gray-100">
              {{ val }}
            </div>
            <div class="text-xs text-gray-500">
              {{ formatMetricLabel(key) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- E-E-A-T -->
    <div v-else-if="activeDimension === 'eeat'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-3">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
          <span v-if="comp.eeat_signals?.overall_eeat_score" class="text-sm font-bold">{{ comp.eeat_signals.overall_eeat_score }}/100</span>
        </div>
        <div v-if="comp.eeat_signals?.signals" class="grid grid-cols-2 gap-2">
          <div v-for="(signal, key) in comp.eeat_signals.signals" :key="key" class="flex items-center gap-2 text-xs">
            <span :class="signal.present ? 'text-green-600' : 'text-gray-400'">{{ signal.present ? '&#10003;' : '&#10007;' }}</span>
            <span class="text-gray-700 dark:text-gray-300 capitalize">{{ key.replace(/_/g, ' ') }}</span>
          </div>
        </div>
        <p v-if="comp.eeat_signals?.summary" class="text-xs text-gray-500 mt-2">
          {{ comp.eeat_signals.summary }}
        </p>
      </div>
    </div>

    <!-- Freshness -->
    <div v-else-if="activeDimension === 'freshness'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
        </div>
        <div v-if="comp.freshness_data && !comp.freshness_data.error" class="text-sm space-y-1">
          <div v-if="comp.freshness_data.publish_date">
            <span class="text-gray-500">Published:</span> {{ comp.freshness_data.publish_date }}
          </div>
          <div v-if="comp.freshness_data.modified_date">
            <span class="text-gray-500">Updated:</span> {{ comp.freshness_data.modified_date }}
          </div>
          <div v-if="comp.freshness_data.age_days">
            <span class="text-gray-500">Age:</span> {{ comp.freshness_data.age_days }} days
          </div>
        </div>
      </div>
    </div>

    <!-- Above-Fold -->
    <div v-else-if="activeDimension === 'above_fold'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
        </div>
        <div v-if="comp.above_fold_structure && !comp.above_fold_structure.error" class="flex flex-wrap gap-2">
          <span v-for="(val, key) in aboveFoldFlags(comp.above_fold_structure)" :key="key" :class="val ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'" class="px-2 py-0.5 rounded text-xs">
            {{ key }}
          </span>
        </div>
      </div>
    </div>

    <!-- Schema -->
    <div v-else-if="activeDimension === 'schema'" class="space-y-4">
      <div v-for="comp in analyzedCompetitors" :key="comp.id" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium">#{{ comp.serp_position }} {{ comp.domain }}</span>
          <span class="text-xs text-gray-500">{{ comp.schema_analysis?.total_schemas || 0 }} schemas</span>
        </div>
        <div v-if="comp.schema_analysis" class="flex flex-wrap gap-2">
          <span v-for="schema in comp.schema_analysis.json_ld_schemas || []" :key="schema.type" class="px-2 py-0.5 rounded text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
            {{ schema.type }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  competitors: { type: Array, required: true },
  serpAnalysis: { type: Object, required: true },
  benchmarkConfidence: { type: Object, default: null }
})

defineEmits(['proceed-to-quality'])

const activeDimension = ref('headings')

const dimensions = [
  { key: 'headings', label: 'Headings' },
  { key: 'titles', label: 'Titles & Meta' },
  { key: 'images', label: 'Images' },
  { key: 'links', label: 'Links' },
  { key: 'readability', label: 'Readability' },
  { key: 'eeat', label: 'E-E-A-T' },
  { key: 'freshness', label: 'Freshness' },
  { key: 'above_fold', label: 'Above-Fold' },
  { key: 'schema', label: 'Schema' }
]

const analyzedCompetitors = computed(() => props.competitors.filter((c) => c.overall_quality_grade))
const failedCompetitors = computed(() => props.competitors.filter((c) => c.extraction_status === 'failed'))

const gradeBadge = (grade) => {
  const map = {
    A: 'bg-green-100 dark:bg-green-900/30 text-green-700',
    B: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700',
    C: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700',
    D: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700',
    F: 'bg-red-100 dark:bg-red-900/30 text-red-700'
  }
  return map[grade] || map.F
}

const classificationColor = (cls) => ({
  seo: 'text-green-600',
  functional: 'text-amber-500',
  formatting: 'text-red-500'
})[cls] || 'text-gray-400'

const classificationIcon = (cls) => ({
  seo: '&#10003;',
  functional: '&#9888;',
  formatting: '&#10007;'
})[cls] || '?'

const formatMetricLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

const aboveFoldFlags = (structure) => ({
  H1: structure.has_h1,
  'Hero Image': structure.has_hero_image,
  Breadcrumbs: structure.has_breadcrumbs,
  TOC: structure.has_table_of_contents,
  Author: structure.has_author_info,
  Date: structure.has_date,
  CTA: structure.has_cta_above_fold
})
</script>
